import { ButtonUtility } from "@components";
import { PauseCircle, Play, XClose } from "@untitledui/icons";
import { Slider } from "@/components/base/slider/slider";
import { cx } from "@/utils/cx";
import type { RoutePlaybackSpeed } from "../hooks";

const SPEED_OPTIONS: RoutePlaybackSpeed[] = [2, 5, 10];

function formatElapsed(ms: number): string {
	const totalSeconds = Math.round(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

type RoutePlaybackBarProps = {
	isPlaying: boolean;
	progress: number;
	elapsedMs: number;
	speed: RoutePlaybackSpeed;
	startLabel: string;
	endLabel: string;
	overSpeedMarkers: number[];
	onToggle: () => void;
	onSeek: (progress: number) => void;
	onSpeedChange: (speed: RoutePlaybackSpeed) => void;
	onClose: () => void;
};

export const RoutePlaybackBar = ({
	isPlaying,
	progress,
	elapsedMs,
	speed,
	startLabel,
	endLabel,
	overSpeedMarkers,
	onToggle,
	onSeek,
	onSpeedChange,
	onClose,
}: RoutePlaybackBarProps) => {
	const handleSliderChange = (value: number | number[]) => {
		const nextProgress = Array.isArray(value) ? value[0] : value;
		onSeek(nextProgress);
	};

	return (
		<div
			className="absolute right-4 bottom-4 left-4 z-1000 flex items-center gap-4 rounded-2xl bg-white px-4 py-3 shadow-lg"
			data-slot="route-playback-bar"
		>
			<ButtonUtility
				color="secondary"
				icon={
					isPlaying ? (
						<PauseCircle className="size-5" />
					) : (
						<Play className="size-5" />
					)
				}
				onClick={onToggle}
				tooltip={isPlaying ? "Pausar" : "Reproduzir"}
			/>

			<div className="flex flex-1 flex-col gap-1">
				<div className="flex items-center justify-between">
					<span className="text-[#344054] text-sm">{startLabel} (Início)</span>
					<span className="text-[#344054] text-sm">{endLabel} (Fim)</span>
				</div>

				<div className="relative">
					<Slider
						hideOutput={false}
						labelFormatter={() => formatElapsed(elapsedMs)}
						labelPosition="top-floating"
						maxValue={1}
						minValue={0}
						onChange={handleSliderChange}
						outputClassName="text-[10px] px-1 py-0.5"
						step={0.001}
						value={[progress]}
					/>
					{overSpeedMarkers.map((markerProgress) => (
						<span
							className="-translate-x-1/2 -translate-y-1/2 pointer-events-none absolute top-1/2 size-2.5 rounded-full bg-error-500 ring-2 ring-white"
							key={markerProgress}
							style={{ left: `${markerProgress * 100}%` }}
							title="Início de excesso de velocidade"
						/>
					))}
				</div>
			</div>

			<div className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 p-1">
				{SPEED_OPTIONS.map((option) => (
					<button
						aria-label={`Velocidade de reprodução ${option}x`}
						aria-pressed={speed === option}
						className={cx(
							"rounded-md px-2 py-1 font-medium text-xs",
							speed === option
								? "bg-brand-solid text-white"
								: "text-[#344054] hover:bg-gray-50"
						)}
						key={option}
						onClick={() => onSpeedChange(option)}
						type="button"
					>
						{option}x
					</button>
				))}
			</div>

			<ButtonUtility
				color="tertiary"
				icon={<XClose className="size-5 text-quaternary" />}
				onClick={onClose}
				tooltip="Fechar reprodução"
			/>
		</div>
	);
};
